import { useState } from 'react';
import { useT } from '../i18n';

/**
 * Password input with a show/hide eye.
 *
 * The value contract is deliberately identical to the <input type="password">
 * it replaces — value in, value out — so every caller's state is untouched.
 * Revealing only swaps the input's type; the value never leaves React state.
 */

interface Props {
  value: string;
  onChange: (value: string) => void;
  /** Passed straight through — signup wants new-password, sign-in current-password. */
  autoComplete?: string;
  id?: string;
  name?: string;
  placeholder?: string;
  /** Labels the input for screen readers when no visible <label> points at it. */
  ariaLabel?: string;
  /** Paints the field as failed, the same way a bare input's `invalid` class does. */
  invalid?: boolean;
}

export function PasswordField({
  value, onChange, autoComplete, id, name, placeholder, ariaLabel, invalid,
}: Props) {
  const { t } = useT();
  const [shown, setShown] = useState(false);

  return (
    <div className="pwfield">
      <input
        id={id}
        name={name}
        type={shown ? 'text' : 'password'}
        autoComplete={autoComplete}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className={invalid ? 'invalid' : ''}
        aria-invalid={invalid || undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {/* type=button: inside a <form> a bare button submits it. */}
      <button
        type="button"
        className="pwfield-eye"
        onClick={() => setShown((s) => !s)}
        aria-label={shown ? t('pwHide') : t('pwShow')}
        aria-pressed={shown}
        title={shown ? t('pwHide') : t('pwShow')}
      >
        <i className={shown ? 'ri-eye-off-line' : 'ri-eye-line'} aria-hidden="true" />
      </button>
    </div>
  );
}
